import { createContext, useContext, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { api } from "../lib/api";

const SeoCtx = createContext(null);
const cache = new Map();
let config = null;

const tag = (selector, create) => {
  let el = document.head.querySelector(selector);
  if (!el) { el = create(); document.head.appendChild(el); }
  return el;
};
const meta = (attr, key, content) => {
  const el = tag(`meta[${attr}="${key}"]`, () => { const m = document.createElement("meta"); m.setAttribute(attr, key); return m; });
  el.setAttribute("content", content || "");
};

export const applyHead = (m) => {
  document.title = m.title || "VetPris";
  meta("name", "description", m.description);
  meta("name", "robots", m.robots || "index,follow");
  meta("property", "og:title", m.title);
  meta("property", "og:description", m.description);
  meta("property", "og:type", "website");
  meta("property", "og:locale", "da_DK");
  meta("property", "og:site_name", "VetPris");
  const canon = tag('link[rel="canonical"]', () => { const l = document.createElement("link"); l.rel = "canonical"; return l; });
  if (m.canonical) { canon.href = m.canonical; meta("property", "og:url", m.canonical); } else canon.remove();
  const ld = tag("script#seo-jsonld", () => { const s = document.createElement("script"); s.id = "seo-jsonld"; s.type = "application/ld+json"; return s; });
  ld.textContent = JSON.stringify(m.jsonld || []);
};

const loadConfig = () => {
  if (config) return;
  config = api.get("/seo/config").then((r) => { if (r.data.google_site_verification) meta("name", "google-site-verification", r.data.google_site_verification); return r.data; }).catch(() => null);
};

export const SeoProvider = ({ children }) => {
  const { pathname } = useLocation();
  const [m, setM] = useState(cache.get(pathname) || null);
  useEffect(() => {
    loadConfig();
    const hit = cache.get(pathname);
    if (hit) { setM(hit); applyHead(hit); return; }
    api.get("/seo/meta", { params: { path: pathname } }).then((r) => { cache.set(pathname, r.data); setM(r.data); applyHead(r.data); }).catch(() => {});
  }, [pathname]);
  return <SeoCtx.Provider value={m}>{children}</SeoCtx.Provider>;
};

export const useSeoMeta = () => useContext(SeoCtx);

export const Breadcrumbs = () => {
  const m = useSeoMeta();
  const { pathname } = useLocation();
  if (!m?.breadcrumbs?.length || m.path !== pathname) return null;
  return (
    <nav aria-label="Brødkrumme" className="container-vp w-full pt-4 text-xs text-[#52706D]" data-testid="breadcrumbs">
      <ol className="flex flex-wrap items-center gap-1">
        {m.breadcrumbs.map((b, i) => (
          <li key={b.path} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3" aria-hidden="true" />}
            {i < m.breadcrumbs.length - 1 ? <Link to={b.path} className="hover:text-[#087F72] hover:underline">{b.name}</Link> : <span aria-current="page" className="text-[#153C3A]">{b.name}</span>}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export const NoIndex = () => {
  useEffect(() => { meta("name", "robots", "noindex,nofollow"); document.title = "Administration | VetPris"; }, []);
  return null;
};
